import { h } from 'preact';
import Store from 'store/store';
import { beforePublish, hasUnsavedChanges, publish, save, isPlaceholder } from 'utils/data';
import * as jsonPointer from 'utils/jsonPointer';
import { Modal, toast } from 'utils/modal';
import { isFocusStudio } from 'utils/studioConfig';

import Button from 'components/button/button';
import ContactLink from 'components/contactLink/contactLink';

import s from 'components/publish/publish.sss';

export default class Publish extends Modal {
	s = s;

	constructor(props) {
		super(props);

		this.cancel = this.cancel.bind(this);
		this.publish = this.publish.bind(this);
		this.saveAndPublish = this.saveAndPublish.bind(this);
	}

	get hasUnsavedChanges() {
		return hasUnsavedChanges(this.props.path);
	}

	get title() {
		if (this.hasUnsavedChanges) return 'Save changes and publish?';

		const isMoviePage = !isFocusStudio() && this.props.path?.startsWith('/movies/');
		if (isMoviePage) {
			const movie = jsonPointer.get(Store.get().data, this.props.path);
			return `Publish ${isPlaceholder(movie) ? 'Holding Page' : 'Movie Platform'}`;
		}
		return 'Publish';
	}

	cancel() {
		this.base.close();
	}

	async publishRaw(saveFirst) {
		this.setState({ publishing: true, error: null });
		try {
			// Run beforePublish actions
			const saveRequired = await beforePublish(this.props.path);

			if (saveFirst || saveRequired) {
				await save(this.props.path);
			}

			await publish(this.props.path);
			toast('Site published!', { icon: 'rocket' });
			this.base.close();
		} catch (err) {
			console.error(err);
			// toast('Error while publishing', {});
			this.setState({ error: { at: new Date(), error: err }, publishing: false });
		}
	}

	async publish() {
		return this.publishRaw();
	}
	async saveAndPublish() {
		return this.publishRaw(true);
	}

	renderErrorMessage() {
		const error = this.state.error;
		if (!error) return null;
		const errorSupportLink = (
			<ContactLink
				subject="Error while publishing content"
				body={[
					`Error publishing ${this.props.path} at ${error?.at.toISOString() || Date.now()}\n`,
					`Browser: ${navigator.userAgent}\n`,
					'Error details:',
					error?.error.message,
					error?.error.stack
				].filter(e => e).join('\n') + '\n'}
			>
				contact us
			</ContactLink>
		);
		return (
			<div class={s.error}>
				Sorry, an error occured while publishing your changes. Please try again later or {errorSupportLink} if the problem persists.
			</div>
		);
	}

	renderContent() {
		const lines = [];

		if (this.hasUnsavedChanges) {
			lines.push('You have unsaved changes. Would you like to save and publish the changes now?');
		}

		const isMoviePage = !isFocusStudio() && this.props.path?.startsWith('/movies/');
		if (isMoviePage) {
			if (lines.length) lines.push(<br />);

			const movie = jsonPointer.get(Store.get().data, this.props.path);
			const publishPlaceholder = isPlaceholder(movie);
			const currentPublishStatus = movie?.commits?.publish?.published_status;

			const pageTypeName = (<strong>{publishPlaceholder ? 'Holding Page' : 'Movie Platform'}</strong>);

			// If publishing for the first time and is a Holding Page
			if (!currentPublishStatus && publishPlaceholder) {
				lines.push(<p>This will publish the {pageTypeName} live.</p>);
			// If publishing a Movie Platform page for the first time
			} else if (!publishPlaceholder && currentPublishStatus !== 'LIVE_MOVIE_PLATFORM') {
				lines.push(<p>This will publish the {pageTypeName} live and remove the holding page.</p>);
			// If publishing updates to an already live page
			} else {
				lines.push(<p>This will publish changes to the {pageTypeName} live.</p>);
			}
		}

		if (!lines.length) {
			lines.push('Are you sure you want to publish?');
		}

		return (
			<p class={this.c('content')}>
				{lines.map(line => (typeof line === 'string' ? <p>{line}</p> : line))}
				{this.renderErrorMessage()}
			</p>
		);
	}

	renderActions() {
		return (
			<div class={this.c('actions')}>
				<Button type="submit" value="CANCEL" key="cancel">Cancel</Button>
				{this.hasUnsavedChanges
					? <Button primary icon="rocket" onClick={this.saveAndPublish} disabled={this.state.publishing} key="save-publish">Save and publish</Button>
					: <Button primary icon="rocket" onClick={this.publish} disabled={this.state.publishing} key="publish">Publish</Button>}
			</div>
		);
	}
}